import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import driverAPI from '../../api/driverAPI';

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-PH', { dateStyle: 'medium' }) : '—';

export default function DriverPayoutPrintPage() {
  const [searchParams] = useSearchParams();
  const driverId   = searchParams.get('driverId')  || '';
  const from       = searchParams.get('from')       || '';
  const to         = searchParams.get('to')         || '';
  const gateSopNum = parseFloat(searchParams.get('gateSop')    || '0') || 0;
  const allowanceNum = parseFloat(searchParams.get('allowance') || '0') || 0;
  const ootNum     = parseFloat(searchParams.get('oot')        || '0') || 0;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!driverId || !from || !to) { setLoading(false); return; }
    driverAPI.getPayoutSummary(driverId, from, to)
      .then((res) => {
        setReport(res.data?.data || null);
        setLoading(false);
        setTimeout(() => window.print(), 400);
      })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (report?.driver) {
      const d = report.driver;
      document.title = `Driver Payout — ${d.nickname || d.name} (${fmtDate(from)} – ${fmtDate(to)})`;
    }
  }, [report]); // eslint-disable-line

  if (loading) return <p style={{ padding: 40 }}>Loading report…</p>;
  if (!report)  return <p style={{ padding: 40 }}>No report data.</p>;

  const d = report.driver;
  const additions            = gateSopNum + allowanceNum + ootNum;
  const totalTruckingAmount = report.totalTruckingAmount || 0;
  const totalRate           = report.waybills.reduce((s, w) => s + (w.truckingRate || 0) * (d?.payoutRate ?? 0), 0);
  const grandTotal          = report.totalAmount || 0;
  const driverPayout        = totalTruckingAmount * (d?.payoutRate ?? 0.10);
  const netPayout           = driverPayout + additions;

  const reportTitle = `Weekly Report — ${d.nickname || d.name}${d.truckColor ? ` - ${d.truckColor}` : ''}${d.truckPlateCode ? ` ${d.truckPlateCode}` : ''} (${fmtDate(report.period.from)} – ${fmtDate(report.period.to)})`;

  return (
    <>
      <style>{`
        body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; }
        @page { size: A4 landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
        h1 { font-size: 12pt; margin: 0 0 2px; text-align: center; text-transform: uppercase; }
        .meta { text-align: center; font-size: 8.5pt; color: #555; margin-bottom: 10px; }
        table { width: 100%; border-collapse: collapse; font-size: 8.5pt; }
        th { background: #f0f0f0; border: 1px solid #ccc; padding: 3px 5px; text-align: left; font-size: 8pt; white-space: nowrap; }
        td { border: 1px solid #ccc; padding: 3px 5px; }
        .text-right { text-align: right; }
        .summary-table { margin-left: auto; border-collapse: collapse; font-size: 9pt; }
        .summary-table td { border: none; padding: 2px 8px 2px 0; }
        .summary-table .val { text-align: right; font-family: monospace; min-width: 100px; }
        .bold { font-weight: bold; }
        .deduct-table { border-collapse: collapse; font-size: 9pt; }
        .deduct-table td { border: none; padding: 2px 12px 2px 0; }
        .footer-row { display: flex; justify-content: space-between; margin-top: 16px; }
      `}</style>

      <h1>{reportTitle}</h1>
      <p className="meta">
        {report.waybillCount} waybill{report.waybillCount !== 1 ? 's' : ''} &nbsp;·&nbsp; Payout Rate: {(d.payoutRate * 100).toFixed(0)}%
      </p>

      <table>
        <thead>
          <tr>
            {['RATE', 'WAYBILL NO.', 'DATE', 'CONSIGNEE', 'DELIVERY ADD', 'VAN NO.', 'VAN SIZE', 'TRUCKING RATE', 'STRIPPING FEE', 'TOTAL'].map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.waybills.length === 0 ? (
            <tr><td colSpan={10} style={{ textAlign: 'center', padding: 12 }}>No waybills found.</td></tr>
          ) : report.waybills.map((wb) => (
            <tr key={wb.waybillId}>
              <td className="text-right">{PHP(wb.truckingRate * (d?.payoutRate ?? 0))}</td>
              <td><strong>{wb.waybillNo}</strong></td>
              <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(wb.date)}</td>
              <td>{wb.consignee}</td>
              <td>{wb.deliveryAdd}</td>
              <td>{wb.vanNo || '—'}</td>
              <td>{wb.vanSize || '—'}</td>
              <td className="text-right">₱{PHP(wb.truckingRate)}</td>
              <td className="text-right">₱{PHP(wb.strippingFee)}</td>
              <td className="text-right"><strong>₱{PHP(wb.total)}</strong></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="footer-row">
        {/* Additions block */}
        <table className="deduct-table">
          <tbody>
            <tr><td>GATE SOP</td><td style={{ textAlign: 'right' }}>{gateSopNum ? `₱${PHP(gateSopNum)}` : '—'}</td></tr>
            <tr><td>ALLOWANCE</td><td style={{ textAlign: 'right' }}>{allowanceNum ? `₱${PHP(allowanceNum)}` : '—'}</td></tr>
            <tr><td>ADD OOT ALLOWANCE</td><td style={{ textAlign: 'right' }}>{ootNum ? `₱${PHP(ootNum)}` : '—'}</td></tr>
          </tbody>
        </table>

        {/* Summary block */}
        <table className="summary-table">
          <tbody>
            <tr>
              <td>TOTAL (RATE)</td>
              <td className="val">₱{PHP(totalRate)}</td>
            </tr>
            <tr>
              <td>GRAND TOTAL</td>
              <td className="val">₱{PHP(grandTotal)}</td>
            </tr>
            {additions > 0 && (
              <tr>
                <td>TOTAL ADDITIONS</td>
                <td className="val">₱{PHP(additions)}</td>
              </tr>
            )}
            <tr style={{ borderTop: '1px solid #ccc' }}>
              <td className="bold">DRIVER PAYOUT ({(d.payoutRate * 100).toFixed(0)}%)</td>
              <td className="val bold">₱{PHP(driverPayout)}</td>
            </tr>
            {additions > 0 && (
              <tr>
                <td className="bold">NET PAYOUT</td>
                <td className="val bold">₱{PHP(netPayout)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="no-print" style={{ marginTop: 20 }}>
        <button onClick={() => window.print()} style={{ padding: '6px 16px' }}>Print</button>
      </div>
    </>
  );
}
